import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnChanges,
  SimpleChanges,
  HostListener,
  ElementRef,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { DropdownManagerService } from '../../services/dropdown-manager.service';
import { Subscription } from 'rxjs';
import { MultiSelectLabelsPipe } from './multi-select-labels.pipe';

@Component({
  selector: 'app-multi-select',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslocoModule, MultiSelectLabelsPipe],
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true,
    },
  ],
})
export class MultiSelectComponent implements ControlValueAccessor, OnChanges, OnInit, OnDestroy {
  @Input() placeholder = '';
  @Input() label = '';
  @Input() id = '';
  @Input() name = '';
  @Input() required = false;
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() errorMessage = '';
  @Input() options: { value: string | number; label: string }[] = [];
  @Input() maxSelection = 0; // 0 means no limit
  @Input() useTranslationForLabels = false; // Whether to use transloco pipe for labels
  @Input() showCountAfter = 3; // Show count instead of individual items after this number
  @Input() height = 'default'; // 'default', 'tall' for custom heights

  @Output() valueChange = new EventEmitter<(string | number)[]>();
  @Output() blur = new EventEmitter<FocusEvent>();
  @Output() focus = new EventEmitter<FocusEvent>();

  private _value: (string | number)[] = [];
  private _touched = false;
  isDropdownOpen = false;
  private dropdownId: string;
  private closeSubscription: Subscription = new Subscription();

  get value(): (string | number)[] {
    return this._value;
  }

  set value(val: (string | number)[]) {
    this._value = val || [];
    this.onChange(this._value); // Always pass the array, not the original val
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  private readonly dropdownManager = inject(DropdownManagerService);
  private readonly translocoService = inject(TranslocoService);

  constructor() {
    this.dropdownId = `multi-select-${Math.random().toString(36).substr(2, 9)}`;
  }

  ngOnInit(): void {
    this.closeSubscription = this.dropdownManager.closeDropdown$.subscribe((dropdownId) => {
      if (dropdownId === this.dropdownId || dropdownId === null) {
        this.isDropdownOpen = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.closeSubscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && this.options.length > 0 && this._value.length > 0) {
      // Trigger change detection when options are loaded
      this.onChange(this._value);
    }
  }

  onSelectChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedOptions = Array.from(selectElement.selectedOptions).map((option) => option.value);

    // Apply max selection limit if specified
    if (this.maxSelection > 0 && selectedOptions.length > this.maxSelection) {
      return;
    }

    this.value = selectedOptions;
    this.valueChange.emit(this.value);
    this.markAsTouched();
  }

  onSelectBlur(event: FocusEvent) {
    this.markAsTouched();
    this.blur.emit(event);
  }

  // Computed property to check if the select has errors
  get hasError(): boolean {
    return this.errorMessage !== '' && this._touched;
  }

  // Computed property to get the error message
  get errorMsg(): string {
    return this.hasError ? this.errorMessage : '';
  }

  onSelectFocus(event: FocusEvent) {
    this.focus.emit(event);
  }

  toggleDropdown(event?: Event) {
    if (!this.disabled) {
      // Prevent event from bubbling to document click handler
      if (event) {
        event.stopPropagation();
      }

      if (this.isDropdownOpen) {
        // Close this dropdown
        this.dropdownManager.closeDropdown(this.dropdownId);
      } else {
        // Open this dropdown (will close others)
        this.dropdownManager.openDropdown(this.dropdownId);
        this.isDropdownOpen = true;
        this.focus.emit(new FocusEvent('focus'));
      }
    }
  }

  toggleOption(optionValue: string | number, event?: Event) {
    if (this.disabled) return;

    // Prevent the click from bubbling up to the document click handler
    if (event) {
      event.stopPropagation();
    }

    const currentIndex = this._value.indexOf(optionValue);
    let newValue: (string | number)[];

    if (currentIndex === -1) {
      // Add option
      if (this.maxSelection > 0 && this._value.length >= this.maxSelection) {
        return; // Don't add if max selection reached
      }
      newValue = [...this._value, optionValue];
    } else {
      // Remove option
      newValue = this._value.filter((val) => val !== optionValue);
    }

    this.value = newValue;
    this.valueChange.emit(this.value);
  }

  markAsTouched() {
    if (!this._touched) {
      this._touched = true;
      this.onTouched();
    }
  }

  isSelected(optionValue: string | number): boolean {
    return this._value.includes(optionValue);
  }

  // ControlValueAccessor implementation
  writeValue(value: (string | number)[]): void {
    // Ensure value is always an array
    this._value = Array.isArray(value) ? value : [];
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Check if the click is outside any multi-select dropdown
    if (!target.closest('.multi-select-dropdown')) {
      this.dropdownManager.closeAllDropdowns();
    }
  }
}
